import store from '../store/index';
import { logout, setSuspended } from '../store/slices/authSlice';

/**
 * Centralized fetch wrapper with automatic 401 handling
 * Automatically adds Authorization header and handles token expiration
 *
 * @param {string} url - API endpoint URL
 * @param {object} options - fetch options (method, headers, body, etc.)
 * @returns {Promise<Response>} - fetch response
 */
export const fetchWithAuth = async (url, options = {}) => {
  // Get token from Redux store
  const state = store.getState();
  const token = state.auth?.token;

  // Prepare headers
  const headers = {
    ...options.headers,
  };

  // Add Authorization header if token exists and not already provided
  if (token && !headers.Authorization && !headers.authorization) {
    headers.Authorization = `Bearer ${token}`;
  }

  // Make the fetch request
  const response = await fetch(url, {
    ...options,
    headers,
  });

  // Check for 401 Unauthorized
  if (response.status === 401) {
    console.warn('[API] 401 Unauthorized - Token expired or invalid. Logging out user...');

    // Dispatch logout action
    store.dispatch(logout());

    // Redirect to login page
    window.location.href = '/login';

    // Throw error to stop further processing
    throw new Error('Unauthorized - Token expired');
  }

  // MER-02: a suspended account is blocked (403) — surface the global "account
  // blocked" popup instead of logging out, so the merchant can read the reason.
  if (response.status === 403) {
    const data = await response.clone().json().catch(() => ({}));
    if (data?.code === 'ACCOUNT_SUSPENDED') {
      store.dispatch(setSuspended({ reason: data.reason || null }));
      throw new Error('Account suspended');
    }
  }

  return response;
};

/**
 * Fetch with auth and automatic JSON parsing
 *
 * @param {string} url - API endpoint URL
 * @param {object} options - fetch options
 * @returns {Promise<any>} - Parsed JSON response
 */
export const fetchWithAuthJSON = async (url, options = {}) => {
  const response = await fetchWithAuth(url, options);

  // Only parse JSON if response is ok
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ message: 'Request failed' }));
    throw new Error(errorData.message || `Request failed with status ${response.status}`);
  }

  return response.json();
};

export default fetchWithAuth;
